import { useState } from 'react'
import { useTranslation } from '@/hooks/useTypedTranslation'
import { useMultilingualBlogs, useDeleteMultilingualBlog, MultilingualBlogItem } from '@/hooks/useMultilingualBlogs'
import { MultilingualBlogEditor } from '@/components/admin/MultilingualBlogEditor'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu'
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { MoreHorizontal, Edit, Trash2, Eye, Calendar, Plus } from 'lucide-react'
import { toast } from 'sonner'

export function BlogManager() {
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language as 'zh' | 'en'
  const [deleteBlogId, setDeleteBlogId] = useState<string | null>(null)
  const [editingBlog, setEditingBlog] = useState<MultilingualBlogItem | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  
  const { data: blogs, isLoading, refetch } = useMultilingualBlogs(currentLanguage)
  const deleteBlog = useDeleteMultilingualBlog()

  const handleDelete = async () => {
    if (!deleteBlogId) return
    
    try {
      await deleteBlog.mutateAsync(deleteBlogId)
      toast.success(t('admin.blogs.deleteSuccess'))
      setDeleteBlogId(null)
    } catch (error) {
      toast.error(t('admin.blogs.deleteError'))
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(i18n.language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  // Show editor when creating or editing
  if (isCreating || editingBlog) {
    return (
      <MultilingualBlogEditor
        blog={editingBlog}
        onClose={() => {
          setEditingBlog(null)
          setIsCreating(false)
        }}
        onSuccess={() => refetch()}
      />
    )
  }

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader>
              <div className="h-4 bg-muted rounded w-3/4"></div>
              <div className="h-3 bg-muted rounded w-1/2"></div>
            </CardHeader>
            <CardContent>
              <div className="h-3 bg-muted rounded w-full mb-2"></div>
              <div className="h-3 bg-muted rounded w-2/3"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold">
          {blogs?.length || 0} Blogs
        </h2>
        <Button onClick={() => setIsCreating(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Multilingual Blog
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {blogs?.map((blog) => (
          <Card key={blog.id} className="relative group hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-lg">
                    {blog.title}
                  </CardTitle>
                  <CardDescription className="mt-1">
                    /{blog.slug}
                  </CardDescription>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => window.open(`/blog/${blog.slug}`, '_blank')}>
                      <Eye className="h-4 w-4 mr-2" />
                      {t('admin.actions.view')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setEditingBlog(blog)}>
                      <Edit className="h-4 w-4 mr-2" />
                      {t('admin.actions.edit')}
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      className="text-red-600"
                      onClick={() => setDeleteBlogId(blog.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {t('admin.actions.delete')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                {blog.description}
              </p>
              
              <div className="flex items-center justify-between mb-3">
                <Badge variant={blog.is_published ? "default" : "secondary"}>
                  {blog.is_published ? t('blog.status.published') : t('blog.status.draft')}
                </Badge>
              </div>

              {blog.published_at && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Calendar className="h-3 w-3" />
                  {formatDate(blog.published_at)}
                </div>
              )}

              {blog.tags && blog.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-3">
                  {blog.tags.slice(0, 3).map((tag, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                  {blog.tags.length > 3 && (
                    <Badge variant="outline" className="text-xs">
                      +{blog.tags.length - 3}
                    </Badge>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <AlertDialog open={!!deleteBlogId} onOpenChange={() => setDeleteBlogId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.blogs.deleteConfirm.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.blogs.deleteConfirm.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
